// WhatsAppSender.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchUsers4manual, fetchWhatsAppRecipients, addWhatsAppRecipient, removeWhatsAppRecipient } from '../../utils/api';
import { User } from '../../utils/types';
import { useAuth } from '../../context/AuthContext';
import { u } from 'framer-motion/dist/types.d-B50aGbjN';
import { use } from 'i18next';
import axios from 'axios';



const WhatsAppSender: React.FC = () => {
    const { t } = useTranslation();
    const { user: loggedInUser, refreshUser } = useAuth(); // Assume refreshUser exists in your AuthContext to re-fetch profile

    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);


    // --- REAL-TIME POLLING LOGIC ---
    // Inside WhatsAppSender.tsx
    useEffect(() => {
        let pollInterval: NodeJS.Timeout;

        // Only poll if the user is currently looking at this page 
        // AND they haven't linked their Telegram yet.
        if (loggedInUser && !loggedInUser.telegram_chat_id) {
            pollInterval = setInterval(() => {
                refreshUser();
            }, 5000); // Check every 5 seconds
        }

        // console.log("WhatsAppSender: Polling for Telegram chat ID every 5 seconds...", loggedInUser?.user_uuid, loggedInUser?.telegram_chat_id);

        // Clean up the interval on component unmount or if user links Telegram

        return () => clearInterval(pollInterval);

    }, [loggedInUser?.telegram_chat_id]);

    // --- INITIAL DATA LOAD ---
    useEffect(() => {
        const loadUsers = async () => {
            setLoading(true);
            try {
                // Fetch all potential users and the already selected recipients in parallel
                const [usersResponse, selectedUsersResponse] = await Promise.all([
                    fetchUsers4manual(1, '', 500, 'name'), // Fetch more users if needed
                    fetchWhatsAppRecipients()
                ]);

                // Filter all potential users to ensure they have phone details
                const filteredUsers = usersResponse.users.filter((user: User) => user.phone && user.phone_country_code);
                setUsers(filteredUsers);

                // The selected users from the DB might not have all details,
                // so we find the full user object from the `filteredUsers` list.
                const fullSelectedUsers = selectedUsersResponse
                    .map(selectedUser => filteredUsers.find((u: User) => u.id === selectedUser.id))
                    .filter((user): user is User => !!user); // Filter out any undefined results

                setSelectedUsers(fullSelectedUsers);

                console.log('Fetched all users and pre-selected recipients.');
            } catch (error) {
                toast.error(t('whatsAppSender.failedToLoadUsers') as string);
                console.error('Failed to fetch users:', error);
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, [t]);

    // SVG Icons
    const TelegramIcon = ({ connected }: { connected: boolean }) => (
        <svg
            viewBox="0 0 24 24"
            className={`w-5 h-5 inline-block ml-2 ${connected ? 'text-blue-500' : 'text-gray-300'}`}
            fill="currentColor"
        >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.69-.52.35-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.24.35-.49.96-.75 3.78-1.65 6.31-2.73 7.57-3.24 3.61-1.45 4.36-1.7 4.85-1.71.11 0 .35.03.5.15.13.12.16.28.18.39.02.09.02.2.01.31z" />
        </svg>
    );


    // --- HANDLE SELECTED USERS ---
    const handleSelectUser = async (userId: string) => {
        if (!userId) return;

        const userToAdd = users.find(u => u.id === parseInt(userId, 10));
        // Add user only if they exist and are not already in the selected list
        if (userToAdd && !selectedUsers.some(su => su.id === userToAdd.id)) {
            try {
                await addWhatsAppRecipient(userToAdd.id);
                setSelectedUsers(prev => [...prev, userToAdd]);
                toast.success(`${userToAdd.name} added to recipients.`);
            } catch (error) {
                toast.error(`Failed to add ${userToAdd.name}.`);
            }
        }


    };

    const handleRemoveUser = async (userId: number) => {
        const userToRemove = selectedUsers.find(u => u.id === userId);
        if (!userToRemove) return;

        try {
            await removeWhatsAppRecipient(userId);
            setSelectedUsers(prev => prev.filter(user => user.id !== userId));
            toast.info(`${userToRemove.name} removed from recipients.`);
        } catch (error) {
            toast.error(`Failed to remove ${userToRemove.name}.`);
        }
    };

    // --- UPDATED SITE PREFIX LOGIC ---
    // Detect prefix based on hostname to handle t1.u8.my and etika.my cases
    const sitePrefix = window.APP_CONFIG.REACT_APP_COMPANY_NAME.includes('etika') || window.location.hostname.includes('etika') || window.location.hostname.includes('t1.u8')
        ? 'etika'
        : 'vektron';

    // --- UPDATED TEST MESSAGE HANDLER ---
    const handleTestTelegram = async (chatId: string | null | undefined, email: string) => {
        if (!chatId) {
            toast.error("Chat ID is missing.");
            return;
        }

        try {
            const secret = 'base64:UNM71BHLODc2iVmrccIr1ghYxji/FkG9qLgOsS/XAzQ=';

            // REMARK: Added '?route=send-message&site=' + sitePrefix to the URL
            // This ensures index.php knows which bot token (Vektron or Etika) to use.
            const response = await fetch(`https://bot.u8.my/index.php?route=send-message&site=${sitePrefix}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chat_id: chatId,
                    message: `<b>Test Success!</b>\nThis is a test message from ${sitePrefix.toUpperCase()} system sent by ${email}`,
                    api_token: secret
                })
            });

            const result = await response.json();

            if (response.ok && result.ok) {
                toast.success(`Test message sent via ${sitePrefix} bot!`);
            } else {
                console.error("Bot Error:", result);
                toast.error(`Failed: ${result.description || "Check bot_log.txt"}`);
            }
        } catch (error) {
            console.error(error);
            toast.error("Connection error to bot.u8.my");
        }
    };


    // Filter out already selected users from the dropdown list
    const availableUsers = users.filter(u => !selectedUsers.some(su => su.id === u.id));

    const [botName, setBotName] = useState('');

    useEffect(() => {
        const response = async () => {
            try {
                const response = await axios.get(window.APP_CONFIG?.API_URL + '/config');
                console.log('Config response for bot name > ', response);
                setBotName(response.data.telegram_bot_name || 'U8_my_bot');
            } catch (error) {
                console.error('Failed to fetch config for bot name:', error);
            }
        }

        response();
    }, []);



    return (
        <div className="p-4 space-y-4">

            {/* // whatsapp sender temp remarks abb 260420 */}
            {/* <div className="p-4 space-y-4">
                <h2 className="text-xl font-semibold">{t('whatsAppSender.title')}</h2>
                <div>
                    <label htmlFor="user-select" className="block text-base md:text-sm font-medium text-gray-700">
                        {t('whatsAppSender.selectUserLabel')}
                    </label>
                    <select
                        id="user-select"
                        value="" // Always reset to the placeholder
                        onChange={(e) => handleSelectUser(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-gray-100"
                        disabled={loading}
                    >
                        <option value="" disabled>{loading ? t('whatsAppSender.loadingUsers') : t('whatsAppSender.selectUserPlaceholder')}</option>
                        {availableUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.name} ({user.phone})
                            </option>
                        ))}
                    </select>
                </div>
                <div>Selected Recipients:</div> */}

            {/* Display selected users as removable pills */}
            {/* <div className="flex flex-wrap gap-2 mt-2 min-h-[2.5rem]">

                    {selectedUsers.map(user => (
                        <div key={user.id} className="flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-1 rounded-full">
                            <span>{user.name} [ {user.phone} ]</span>
                            <button
                                onClick={() => handleRemoveUser(user.id)}
                                className="ml-2 -mr-1 p-1 flex items-center justify-center text-blue-600 hover:bg-blue-200 rounded-full"
                                aria-label={`Remove ${user.name}`}
                            >&times;</button>
                        </div>
                    ))}
                </div>

            </div> */}

            <div className="whatsapp-sender-container">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                    {t('taskReminder.recipients')}
                    <span className="ml-2 text-xs font-normal text-gray-500">(Telegram)</span>
                </h3>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    // <ul className="space-y-2">
                    //     {recipients.map((user) => (
                    //         <li key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                    //             <div className="flex items-center">
                    //                 <span className="font-medium text-gray-800">{user.name}</span>

                    //                 {/* Telegram Status Icon */}
                    //                 <div title={user.telegram_chat_id ? 'Telegram Linked' : 'Telegram Not Linked'}>
                    //                     <TelegramIcon connected={!!user.telegram_chat_id} />
                    //                 </div>
                    //             </div>

                    //             <div className="text-right">
                    //                 {/* Current WhatsApp Number (Keep for future/current use) */}
                    //                 <div className="text-xs text-gray-500">
                    //                     {user.phone_country_code}{user.phone}
                    //                 </div>

                    //                 {/* Connection Label */}
                    //                 <span className={`text-[10px] px-2 py-0.5 rounded-full ${user.telegram_chat_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                    //                     {user.telegram_chat_id ? 'Bot Active' : 'WA Only'}
                    //                 </span>
                    //             </div>
                    //         </li>
                    //     ))}
                    //     </ul>

                    <div className="flex flex-col gap-2 mt-2 min-h-[2.5rem]">

                        <div>
                            <label htmlFor="user-select" className="block text-base md:text-sm font-medium text-gray-700">
                                {t('whatsAppSender.selectUserLabel')}
                            </label>
                            <select
                                id="user-select"
                                value="" // Always reset to the placeholder
                                onChange={(e) => handleSelectUser(e.target.value)}
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-gray-100"
                                disabled={loading}
                            >
                                <option value="" disabled>{loading ? t('whatsAppSender.loadingUsers') : t('whatsAppSender.selectUserPlaceholder')}</option>
                                {availableUsers.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} ({user.phone})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className='flex flex-col md:flex-col '>
                            <label htmlFor="user-select" className="block text-base md:text-sm font-medium text-gray-700">Selected Recipients:</label>

                            {selectedUsers.map(user => (
                                <div className='flex flex-col' key={user.id}>

                                    <div className="flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-1 rounded-full">
                                        <span>{user.name} [ {user.phone} ]
                                            <span className="text-sm text-gray-700">{user?.email} {user?.telegram_chat_id ? '🟢' : '🔵'}</span>
                                        </span>


                                        <div className="mt-3 mx-3 flex flex-row justify-center items-center gap-2">
                                            {/* <span className="text-sm text-gray-700">{user?.email} {user?.telegram_chat_id ? '🟢' : '🔵'}</span> */}

                                            {user?.telegram_chat_id ? (

                                                <div>
                                                    <button
                                                        onClick={() => handleTestTelegram(user.telegram_chat_id, user.email)}
                                                        className="text-xs bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-full font-bold flex items-center transition-colors"
                                                    >
                                                        <i className="mr-1">✓</i> {sitePrefix} test message
                                                    </button>
                                                </div>

                                            ) : (
                                                <div>

                                                    {loggedInUser?.user_uuid === user?.user_uuid && (
                                                        <a
                                                            href={`https://t.me/${botName}?start=${sitePrefix}_${loggedInUser?.user_uuid}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                                                        >
                                                            Link My Own Telegram
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => handleRemoveUser(user.id)}
                                            className="ml-2 -mr-1 p-1 flex items-center justify-center text-blue-600 hover:bg-blue-200 rounded-full"
                                            aria-label={`Remove ${user.name}`}
                                        >&times;</button>
                                    </div>


                                </div>
                            ))}

                        </div>

                    </div>
                )}

                {/* Link Button for the current logged in user */}
                {/* <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-800">
                        <strong>Note:</strong> Blue icon means the user has linked <strong>bot.u8.my</strong>.
                        System will prioritize Telegram. If the icon is gray, system will use <strong>wa.u8.my</strong>.
                    </p>

                    <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-800 mb-2">
                            <strong>Note:</strong> System prioritizes <strong>Telegram (Blue)</strong>. Gray icons fallback to <strong>wa.u8.my</strong>.
                        </p>


                        <div className="mt-3 flex items-center gap-2">
                            <span className="text-sm text-gray-700">{loggedInUser?.email} {loggedInUser?.telegram_chat_id ? '🟢' : '🔵'}</span>

                            {loggedInUser?.telegram_chat_id ? (
                                <span className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-full font-bold flex items-center">
                                    <i className="mr-1">✓</i> Connected!!!
                                </span>
                            ) : (
                                <a
                                    href={`https://t.me/U8_my_bot?start=${loggedInUser?.user_uuid}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                                >
                                    Link My Own Telegram
                                </a>
                            )}
                        </div>


                    </div>


                </div> */}


            </div>

        </div>

    );
};

export default WhatsAppSender;